import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getTodayDateString } from '../../../utils/dateFormatters';
import {
  RestaurantPurchaseOrderDetailForm,
  RestaurantPurchaseOrderDTO,
  RestaurantPurchaseOrderForm,
  RestaurantPurchaseOrderOptions,
} from '../../../types/restaurant/RestaurantPurchaseOrderTypes';
import { IDropdownSupplier } from '../../../types/supplier/supplierInterfaces';
import { RestaurantRawMaterialDTO } from '../../../types/restaurant/RestaurantRawMaterialTypes';
import { RestaurantRawMaterialCategoryDTO } from '../../../types/restaurant/RestaurantRawMaterialCategoryTypes';
import { RestaurantUnitConversionDTO } from '../../../types/restaurant/RestaurantUnitConversionTypes';
import { PageMetadata } from '../../../types/restaurant/PagedResponse';

const newDetail = (key: number): RestaurantPurchaseOrderDetailForm => ({
  key,
  rawMaterialId: '',
  quantity: 1,
  unitId: '',
  unitCost: 0,
  discountAmount: 0,
  taxAmount: 0,
  expirationDate: '',
  lotNumber: '',
});

const emptyForm = (): RestaurantPurchaseOrderForm => ({
  supplierId: '',
  purchaseDate: getTodayDateString(),
  paymentCondition: null,
  notes: '',
  details: [newDetail(1)],
});

interface State extends RestaurantPurchaseOrderOptions {
  list: RestaurantPurchaseOrderDTO[];
  page: PageMetadata | null;
  selected: RestaurantPurchaseOrderDTO | null;
  form: RestaurantPurchaseOrderForm;
  loading: boolean;
  loadingOptions: boolean;
  saving: boolean;
  actingId: number | null;
  formOpen: boolean;
  detailOpen: boolean;
  nextDetailKey: number;
}

const initialState: State = {
  list: [],
  page: null,
  selected: null,
  form: emptyForm(),
  suppliers: [],
  rawMaterials: [],
  categories: [],
  units: [],
  conversions: [],
  paymentConditions: [],
  terms: [],
  loading: false,
  loadingOptions: false,
  saving: false,
  actingId: null,
  formOpen: false,
  detailOpen: false,
  nextDetailKey: 2,
};

const slice = createSlice({
  name: 'restaurantPurchaseOrder',
  initialState,
  reducers: {
    setPurchaseOrders: (
      state,
      action: PayloadAction<{
        content: RestaurantPurchaseOrderDTO[];
        page: PageMetadata;
      }>
    ) => {
      state.list = action.payload.content;
      state.page = action.payload.page;
    },
    setPurchaseOrderOptions: (
      state,
      action: PayloadAction<RestaurantPurchaseOrderOptions>
    ) => {
      Object.assign(state, action.payload);
    },
    // Materia prima recién creada al vuelo desde el modal de compra.
    appendPurchaseOrderRawMaterial: (
      state,
      action: PayloadAction<RestaurantRawMaterialDTO>
    ) => {
      if (!state.rawMaterials.some((item) => item.id === action.payload.id)) {
        state.rawMaterials = [...state.rawMaterials, action.payload].sort(
          (a, b) => a.name.localeCompare(b.name)
        );
      }
    },
    appendPurchaseOrderCategory: (
      state,
      action: PayloadAction<RestaurantRawMaterialCategoryDTO>
    ) => {
      if (!state.categories.some((item) => item.id === action.payload.id)) {
        state.categories = [...state.categories, action.payload];
      }
    },
    // Refresca la lista de proveedores tras registrar uno desde el modal.
    setPurchaseOrderSuppliers: (
      state,
      action: PayloadAction<IDropdownSupplier[]>
    ) => {
      state.suppliers = action.payload;
    },
    // Refresca las reglas de conversión (p. ej. tras crear una equivalencia de
    // empaque en el asistente de importación de DTE).
    setPurchaseOrderConversions: (
      state,
      action: PayloadAction<RestaurantUnitConversionDTO[]>
    ) => {
      state.conversions = action.payload;
    },
    setPurchaseOrderLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setPurchaseOrderOptionsLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingOptions = action.payload;
    },
    setPurchaseOrderSaving: (state, action: PayloadAction<boolean>) => {
      state.saving = action.payload;
    },
    setPurchaseOrderActing: (state, action: PayloadAction<number | null>) => {
      state.actingId = action.payload;
    },
    openPurchaseOrderCreate: (state) => {
      state.form = emptyForm();
      state.nextDetailKey = 2;
      state.formOpen = true;
    },
    openPurchaseOrderEdit: (
      state,
      action: PayloadAction<RestaurantPurchaseOrderForm>
    ) => {
      state.form = action.payload;
      state.nextDetailKey =
        Math.max(0, ...action.payload.details.map((item) => item.key)) + 1;
      state.formOpen = true;
    },
    closePurchaseOrderForm: (state) => {
      state.form = emptyForm();
      state.formOpen = false;
    },
    openPurchaseOrderDetail: (
      state,
      action: PayloadAction<RestaurantPurchaseOrderDTO>
    ) => {
      state.selected = action.payload;
      state.detailOpen = true;
    },
    closePurchaseOrderDetail: (state) => {
      state.selected = null;
      state.detailOpen = false;
    },
    updatePurchaseOrderField: (
      state,
      action: PayloadAction<{
        field: keyof Omit<RestaurantPurchaseOrderForm, 'id' | 'details'>;
        value: RestaurantPurchaseOrderForm[
          keyof Omit<RestaurantPurchaseOrderForm, 'id' | 'details'>
        ];
      }>
    ) => {
      (state.form[action.payload.field] as never) = action.payload.value as never;
    },
    addPurchaseOrderDetail: (state) => {
      state.form.details.push(newDetail(state.nextDetailKey++));
    },
    removePurchaseOrderDetail: (state, action: PayloadAction<number>) => {
      state.form.details = state.form.details.filter(
        (item) => item.key !== action.payload
      );
    },
    updatePurchaseOrderDetail: (
      state,
      action: PayloadAction<{
        key: number;
        field: keyof Omit<RestaurantPurchaseOrderDetailForm, 'key'>;
        value: string | number;
      }>
    ) => {
      const detail = state.form.details.find(
        (item) => item.key === action.payload.key
      );
      if (detail) detail[action.payload.field] = action.payload.value as never;
    },
  },
});

export const {
  setPurchaseOrders,
  setPurchaseOrderOptions,
  appendPurchaseOrderRawMaterial,
  appendPurchaseOrderCategory,
  setPurchaseOrderSuppliers,
  setPurchaseOrderConversions,
  setPurchaseOrderLoading,
  setPurchaseOrderOptionsLoading,
  setPurchaseOrderSaving,
  setPurchaseOrderActing,
  openPurchaseOrderCreate,
  openPurchaseOrderEdit,
  closePurchaseOrderForm,
  openPurchaseOrderDetail,
  closePurchaseOrderDetail,
  updatePurchaseOrderField,
  addPurchaseOrderDetail,
  removePurchaseOrderDetail,
  updatePurchaseOrderDetail,
} = slice.actions;
export default slice.reducer;
